/**
 * Peças comuns do portal do cliente: a consulta única que alimenta todas as páginas (uma chamada traz tudo e toda ação devolve
 * a tela inteira de novo), os ícones, e os formatos de data e valor escritos como gente fala ("hoje", "amanhã", "há 3 dias").
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import type { PortalEu, PortalInicio } from '@sgc/contracts';
import { get } from '../../lib/api';

export const CHAVE_INICIO = ['portal', 'inicio'] as const;

export function usePortalEu() {
  return useQuery({ queryKey: ['portal', 'eu'], queryFn: () => get<PortalEu>('/v1/portal/eu'), retry: false, staleTime: 60_000 });
}
/** Tudo o que o portal mostra. Atualiza sozinho a cada minuto e quando a pessoa volta para a aba. */
export function usePortal(ativo = true) {
  return useQuery({ queryKey: CHAVE_INICIO, queryFn: () => get<PortalInicio>('/v1/portal/inicio'), enabled: ativo, refetchInterval: 60_000, refetchOnWindowFocus: true });
}
/** Ação do portal: o servidor devolve a tela inteira e ela entra direto no cache — nada de segunda chamada, nada de piscar. */
export function useAcao<V>(fn: (v: V) => Promise<PortalInicio>, aoTerminar?: (r: PortalInicio, v: V) => void) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (r, v) => { qc.setQueryData(CHAVE_INICIO, r); aoTerminar?.(r, v); } });
}
export const erroDe = (e: unknown): string | null => (e ? (e instanceof Error ? e.message : 'não deu certo — tente de novo') : null);
/** Chegou com `#g-<id>` na URL (aviso da central, push, e-mail): rola até o cartão quando os dados estão na tela e o acende por uns segundos. */
export function useRolarAte(pronto: boolean) {
  useEffect(() => {
    if (!pronto) return;
    const h = window.location.hash.slice(1);
    if (!h) return;
    const el = document.getElementById(h);
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    el.classList.add('pt-alvo');
    const t = setTimeout(() => el.classList.remove('pt-alvo'), 2500);
    return () => clearTimeout(t);
  }, [pronto]);
}

// ------------------------------------------------------------------ ícones (traço, 24x24)

const CAMINHOS = {
  casa: 'M3 11.5 12 4l9 7.5M5.5 10v9.5h13V10',
  mais: 'M12 5v14M5 12h14',
  lista: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  arquivo: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6',
  conversa: 'M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z',
  empresa: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M3 21h18M8 8h3M8 12h3M8 16h3',
  sino: 'M18 9a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 15 18 9zM10 20a2 2 0 0 0 4 0',
  baixar: 'M12 4v11M7.5 11 12 15.5 16.5 11M5 20h14',
  caneta: 'M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17zM14 7l3 3',
  certo: 'M5 12.5 10 17.5 19 7',
  relogio: 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  alerta: 'M12 9v4M12 17h.01M10.3 4.3 2.6 17.6A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-2.9L13.7 4.3a2 2 0 0 0-3.4 0z',
  enviar: 'M12 16V5M7.5 9 12 4.5 16.5 9M5 20h14',
  seta: 'M5 12h14M13 6l6 6-6 6',
  voltar: 'M19 12H5M11 6l-6 6 6 6',
  busca: 'M20 20l-4.2-4.2M17 10.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0z',
  fechar: 'M6 6l12 12M18 6 6 18',
  pessoa: 'M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM4 21a8 8 0 0 1 16 0',
  pessoas: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 6.5M18.5 14.6a6.5 6.5 0 0 1 3 5.4',
  sair: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 8l-4 4 4 4M6 12h10',
  chave: 'M14.5 9.5a4.5 4.5 0 1 0-4.2 4.5L3 21h4v-3h3v-3h2.3a4.5 4.5 0 0 0 2.2-5.5zM17 8h.01',
  dinheiro: 'M3 7h18v10H3zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6.5 10v.01M17.5 14v.01',
  clipe: 'M20 11.5 12 19.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L9.7 17.2a1.7 1.7 0 0 1-2.4-2.4L15 7',
  megafone: 'M4 10v4a1 1 0 0 0 1 1h2l5 4V5L7 9H5a1 1 0 0 0-1 1zM16 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11',
  calendario: 'M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
  nota: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6',
  maleta: 'M4 8h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM9 8V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18',
} as const;
export type NomeIcone = keyof typeof CAMINHOS;
export function Ic({ n, titulo }: { n: NomeIcone; titulo?: string }) {
  return <svg className="ic" viewBox="0 0 24 24" aria-hidden={titulo ? undefined : true} role={titulo ? 'img' : undefined}>{titulo && <title>{titulo}</title>}<path d={CAMINHOS[n]} /></svg>;
}

// ------------------------------------------------------------------ formatos

const hojeLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
const diasEntre = (de: string, ate: string) => Math.round((new Date(`${ate}T00:00:00Z`).getTime() - new Date(`${de}T00:00:00Z`).getTime()) / 86_400_000);
export const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
export const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** dia/mês de um INSTANTE (carimbo com hora, em UTC): no fuso do Brasil — fatiar a string daria o dia seguinte depois das 21h */
export const dataCurtaDe = (isoInstante: string) => dataCurta(new Date(isoInstante).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }));
/** "AAAA-MM" de um instante, no fuso do Brasil (um envio às 22h de 31/10 é de outubro, não de novembro) */
export const mesLocalDe = (isoInstante: string) => new Date(isoInstante).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
export const moeda = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const tamanho = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);
/** prazo dito como gente fala: "vence hoje", "vence amanhã", "vence em 5 dias (24/09)", "venceu há 3 dias" */
export function prazoFalado(iso: string, verbo: [string, string] = ['vence', 'venceu']): string {
  const d = diasEntre(hojeLocal(), iso);
  if (d === 0) return `${verbo[0]} hoje`;
  if (d === 1) return `${verbo[0]} amanhã`;
  if (d > 1) return `${verbo[0]} em ${d} dias (${dataCurta(iso)})`;
  return d === -1 ? `${verbo[1]} ontem` : `${verbo[1]} há ${-d} dias (${dataCurta(iso)})`;
}
/** momento dito como gente fala: "agora há pouco", "hoje às 14:20", "ontem", "há 5 dias", "12/08/2026" */
export function quando(isoDataHora: string): string {
  const t = new Date(isoDataHora); const min = Math.round((Date.now() - t.getTime()) / 60_000);
  if (min < 2) return 'agora há pouco';
  if (min < 60) return `há ${min} min`;
  const dia = t.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }); const d = diasEntre(dia, hojeLocal());
  const hora = t.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
  if (d === 0) return `hoje às ${hora}`;
  if (d === 1) return `ontem às ${hora}`;
  if (d < 7) return `há ${d} dias`;
  return dataBr(dia);
}
export const primeiroNome = (nome: string) => { const n = nome.trim().split(/\s+/)[0] ?? nome; return n === n.toUpperCase() && n.length > 1 ? n.charAt(0) + n.slice(1).toLowerCase() : n; };
export function saudacao(): string {
  const h = Number(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', hour12: false, timeZone: 'America/Sao_Paulo' }).slice(0, 2));
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

// ------------------------------------------------------------------ peças pequenas

export function Secao({ titulo, sub, mais, children }: { titulo: string; sub?: string; mais?: ReactNode; children: ReactNode }) {
  return <section className="pt-sec"><div className="cab"><h2>{titulo}</h2>{sub && <span className="sub">{sub}</span>}{mais}</div>{children}</section>;
}
export function Vazio({ titulo, children, icone = 'certo' }: { titulo: string; children?: ReactNode; icone?: NomeIcone }) {
  return <div className="pt-card pt-vazio"><div className="bolha"><Ic n={icone} /></div><b>{titulo}</b>{children && <p>{children}</p>}</div>;
}
export function Chip({ cor, children }: { cor?: 'verm' | 'ambar' | 'verde' | 'azul' | 'novo'; children: ReactNode }) {
  return <span className={`pt-chip${cor ? ` ${cor}` : ''}`}>{children}</span>;
}
/** hora de um instante, no fuso do Brasil ("14:32") */
export const horaDe = (isoInstante: string) => new Date(isoInstante).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
/** "Mais ▾": as ações secundárias de um cartão (details/summary). Escolher um item fecha o menu — senão ele fica aberto por cima do que o item abriu (o formulário "enviar para outro e-mail", no celular). */
export function MenuMais({ children, rotulo = 'Mais ▾' }: { children: ReactNode; rotulo?: string }) {
  return <details className="pt-mais"><summary className="btn">{rotulo}</summary><div className="pt-mais-itens" onClick={(e) => { if ((e.target as HTMLElement).closest('a,button')) (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open'); }}>{children}</div></details>;
}
/** como um arquivo chegou ao cliente: "recebida em 20/10 por e-mail" / "no portal desde 20/10" / "recebida em 20/10" (antes do controle de envios) */
export const COMO_CHEGOU: Record<string, string> = { EMAIL: 'por e-mail', WHATSAPP: 'por WhatsApp', ZIP: 'pelo escritório', PORTAL: '', ANTIGO: '' };
/**
 * Baixa por fetch, não por navegação: um 429 ("muitos arquivos nesta hora"), 400 ou 409 vira uma frase ao lado do link —
 * nunca uma página de JSON no lugar do portal. O nome do arquivo vem do servidor (content-disposition).
 */
export function useBaixar() {
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const baixar = async (href: string) => {
    setErro(null); setOcupado(true);
    try {
      const r = await fetch(href, { credentials: 'same-origin' });
      if (!r.ok) {
        let m = r.status === 429 ? 'muitos downloads agora — tente de novo mais tarde' : `não deu certo (${r.status})`;
        try { const j = (await r.json()) as { erro?: string }; if (j?.erro) m = j.erro; } catch { /* sem corpo */ }
        setErro(m); return;
      }
      const blob = await r.blob();
      const m = /filename\*=UTF-8''([^;]+)/i.exec(r.headers.get('content-disposition') ?? '');
      const nome = m ? decodeURIComponent(m[1]!) : 'arquivo';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { setErro('não deu certo — tente de novo'); } finally { setOcupado(false); }
  };
  return { baixar, erro, ocupado };
}
/** Link que baixa pelo `useBaixar` (continua um link: dá para copiar o endereço) e mostra o erro ao lado. */
export function LinkBaixar({ href, className = 'mais', title, children }: { href: string; className?: string; title?: string; children: ReactNode }) {
  const { baixar, erro, ocupado } = useBaixar();
  return <><a className={className} href={href} title={title} aria-busy={ocupado || undefined} onClick={(e) => { e.preventDefault(); void baixar(href); }}>{ocupado ? 'preparando…' : children}</a>{erro && <span className="meta" role="alert" style={{ color: 'var(--red)', whiteSpace: 'normal' }}> {erro}</span>}</>;
}
export function comoChegou(e: { quando: string; como: string } | null | undefined, feminino = true): string | null {
  if (!e) return null;
  const dia = dataCurtaDe(e.quando);
  if (e.como === 'PORTAL') return `no portal desde ${dia}`;
  const c = COMO_CHEGOU[e.como] ?? '';
  return `${feminino ? 'recebida' : 'recebido'} em ${dia}${c ? ` ${c}` : ''}`;
}
/** Botão que abre o seletor de arquivos do aparelho (no celular oferece câmera e galeria). Aceita vários de uma vez. */
export function BotaoArquivo({ rotulo, ocupado, onArquivos, principal }: { rotulo: string; ocupado?: boolean; onArquivos: (fs: File[]) => void; principal?: boolean }) {
  return (
    <label className={`btn${principal ? ' primario' : ''}`} style={ocupado ? { opacity: .6, pointerEvents: 'none' } : undefined}>
      <input type="file" multiple style={{ display: 'none' }} onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.target.value = ''; if (fs.length) onArquivos(fs); }} />
      <Ic n="clipe" /> {ocupado ? 'enviando…' : rotulo}
    </label>
  );
}
